/**
 * Presenter beams: while someone is in "Point" mode, a thin ray from their hand to what they
 * point at is drawn for everyone else, with a dot at the target. Senders throttle to 10 Hz;
 * receivers keep one line per sender and drop it half a second after the last update.
 */
import * as THREE from 'three';
import type { InputManager } from '../xr/InputManager.ts';

interface Beam {
  line: THREE.Line;
  dot: THREE.Mesh;
  lastUpdate: number;
  name: string;
}

const SEND_INTERVAL_MS = 100;
const STALE_MS = 500;

export class Beams {
  readonly group = new THREE.Group();
  private readonly beams = new Map<string, Beam>();
  private readonly material = new THREE.LineBasicMaterial({
    color: 0x35b5b0,
    transparent: true,
    opacity: 0.85,
  });
  private readonly dotGeometry = new THREE.SphereGeometry(0.025, 10, 8);
  private readonly dotMaterial = new THREE.MeshBasicMaterial({ color: 0x35b5b0 });
  private lastSent = 0;
  private wasPointing = false;
  /** Dev hook: the last beam received. */
  lastReceived: { from: string; target: [number, number, number] | null } | undefined;

  constructor(
    private readonly input: InputManager,
    private readonly send: (
      origin: [number, number, number],
      target: [number, number, number] | null,
    ) => void,
  ) {}

  /** Sender side: call every frame with whether the local user is pointing. */
  update(pointing: boolean, now = performance.now()): void {
    if (pointing && now - this.lastSent >= SEND_INTERVAL_MS) {
      const p =
        this.input.activePointers().find((x) => x.handedness === 'right') ??
        this.input.activePointers()[0];
      if (p) {
        const o = p.ray.origin;
        const target =
          p.hitDistance !== undefined
            ? p.ray.at(p.hitDistance, new THREE.Vector3())
            : p.ray.at(4, new THREE.Vector3());
        this.send([r(o.x), r(o.y), r(o.z)], [r(target.x), r(target.y), r(target.z)]);
        this.lastSent = now;
      }
    } else if (!pointing && this.wasPointing) {
      const p = this.input.activePointers()[0];
      if (p) this.send([r(p.ray.origin.x), r(p.ray.origin.y), r(p.ray.origin.z)], null);
    }
    this.wasPointing = pointing;
    for (const [id, b] of this.beams) {
      if (now - b.lastUpdate > STALE_MS) this.remove(id);
    }
  }

  /** Receiver side. */
  onBeam(
    from: { sessionId: string; name: string },
    origin: [number, number, number],
    target: [number, number, number] | null,
  ): void {
    this.lastReceived = { from: from.name, target };
    if (!target) {
      this.remove(from.sessionId);
      return;
    }
    let b = this.beams.get(from.sessionId);
    if (!b) {
      const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(),
        new THREE.Vector3(),
      ]);
      const line = new THREE.Line(geometry, this.material);
      line.frustumCulled = false;
      const dot = new THREE.Mesh(this.dotGeometry, this.dotMaterial);
      this.group.add(line, dot);
      b = { line, dot, lastUpdate: 0, name: from.name };
      this.beams.set(from.sessionId, b);
    }
    const pos = b.line.geometry.getAttribute('position') as THREE.BufferAttribute;
    pos.setXYZ(0, origin[0], origin[1], origin[2]);
    pos.setXYZ(1, target[0], target[1], target[2]);
    pos.needsUpdate = true;
    b.dot.position.set(target[0], target[1], target[2]);
    b.lastUpdate = performance.now();
  }

  get activeCount(): number {
    return this.beams.size;
  }

  private remove(id: string): void {
    const b = this.beams.get(id);
    if (!b) return;
    this.group.remove(b.line, b.dot);
    b.line.geometry.dispose();
    this.beams.delete(id);
  }

  dispose(): void {
    for (const id of [...this.beams.keys()]) this.remove(id);
    this.material.dispose();
    this.dotGeometry.dispose();
    this.dotMaterial.dispose();
  }
}

function r(v: number): number {
  return Math.round(v * 1000) / 1000;
}
